import React, { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import api, { BACKEND_URL } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Reusable avatar with click-to-upload.
 * Props:
 *   userId      – user_id whose photo to update
 *   name        – display name (for initials fallback)
 *   pictureUrl  – current picture URL (relative /api/files/… or full URL)
 *   size        – tailwind size class e.g. "w-20 h-20"
 *   onUploaded  – (newPictureUrl) => void
 *   readOnly    – if true, no upload button shown
 */
export function AvatarUpload({ userId, name, pictureUrl, size = 'w-20 h-20', onUploaded, readOnly = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState(null);

  const displayUrl = localUrl || (pictureUrl ? (pictureUrl.startsWith('http') ? pictureUrl : `${BACKEND_URL}${pictureUrl}`) : null);
  const initials = name ? name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/auth/users/${userId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrl = `${BACKEND_URL}${res.data.picture}`;
      setLocalUrl(newUrl);
      onUploaded?.(res.data.picture);
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="relative inline-block group">
      <div className={`${size} rounded-xl bg-[#1565C0] flex items-center justify-center overflow-hidden text-white font-bold text-2xl`}>
        {displayUrl ? (
          <img src={displayUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
      </div>
      {!readOnly && !uploading && (
        <button
          onClick={() => fileRef.current?.click()}
          className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#2E6CC4] border-2 border-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
          title="Change photo"
        >
          <Camera className="w-3.5 h-3.5 text-white" />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFile} />
    </div>
  );
}
